import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Download, Eye, EyeOff, Globe } from "lucide-react";
import AthleteBadges from "@/components/AthleteBadges";

const MEDAL_COLORS = ["#FFCC00", "#C0C0C0", "#CD7F32"];

export default function Ranking() {
  const { currentId } = useCompetition();
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadCats = () => {
    if (!currentId) return;
    api.get(`/competitions/${currentId}/categories`).then((r) => {
      setCats(r.data);
      if (!categoryId && r.data[0]) setCategoryId(r.data[0].id);
    });
  };

  useEffect(() => {
    loadCats();
    // eslint-disable-next-line
  }, [currentId]);

  useEffect(() => {
    if (!currentId || !categoryId) return;
    api
      .get("/ranking", { params: { competition_id: currentId, category_id: categoryId } })
      .then((r) => setData(r.data));
  }, [currentId, categoryId]);

  const currentCat = cats.find((c) => c.id === categoryId);
  const published = !!currentCat?.results_published;

  const togglePublish = async () => {
    if (!categoryId) return;
    setBusy(true);
    try {
      await api.patch(`/categories/${categoryId}/publish`, { published: !published });
      loadCats();
    } finally {
      setBusy(false);
    }
  };

  const bulkPublish = async (value) => {
    if (!currentId) return;
    if (!window.confirm(
      value
        ? "Publicar TODOS os rankings desta competição para o público?"
        : "Ocultar TODOS os rankings desta competição do público?"
    )) return;
    setBusy(true);
    try {
      await api.post(`/competitions/${currentId}/results/publish-all`, { published: value });
      loadCats();
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/ranking.csv?competition_id=${currentId}&category_id=${categoryId}`;
    window.open(url, "_blank");
  };

  const exportPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/ranking.pdf?competition_id=${currentId}&category_id=${categoryId}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Resultados</div>
          <h1 className="font-display text-5xl font-black tracking-tight">CLASSIFICAÇÃO</h1>
        </div>
        <CompetitionSelector />
      </div>

      <div className="card p-5 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <label className="overline block mb-2">Categoria</label>
          <select
            data-testid="ranking-categoria"
            className="input-field"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.results_published ? "🌐 " : ""}{c.nome}
              </option>
            ))}
          </select>
        </div>
        <button data-testid="ranking-export" className="btn-ghost flex items-center gap-2" onClick={exportCsv}>
          <Download size={16} /> Exportar CSV
        </button>
        <button data-testid="ranking-export-pdf" className="btn-primary flex items-center gap-2" onClick={exportPdf}>
          <Download size={16} /> PDF Oficial
        </button>
      </div>

      <div
        className="card p-4 mb-4 flex flex-wrap items-center gap-3"
        data-testid="publish-panel"
      >
        <Globe size={16} style={{ color: "#C9A84C" }} />
        <div className="flex-1 min-w-[220px]">
          <div className="overline">Divulgação Pública</div>
          <div className="text-sm text-[var(--text-secondary)]">
            {currentCat
              ? published
                ? "Este ranking está VISÍVEL para o público em /publico."
                : "Este ranking está OCULTO — perfeito para revelação no palco."
              : "Selecione uma categoria."}
          </div>
        </div>
        <button
          data-testid="publish-toggle"
          onClick={togglePublish}
          disabled={!categoryId || busy}
          className="flex items-center gap-2 py-2 px-4 font-bold text-xs tracking-[0.25em] uppercase transition disabled:opacity-40"
          style={
            published
              ? {
                  background: "#16FF7A",
                  color: "#062",
                  boxShadow: "0 0 12px rgba(22,255,122,0.35)",
                }
              : {
                  background: "transparent",
                  color: "#C9A84C",
                  border: "1px solid #C9A84C",
                }
          }
        >
          {published ? (
            <>
              <Eye size={14} /> Publicado
            </>
          ) : (
            <>
              <EyeOff size={14} /> Ocultar
            </>
          )}
        </button>
        <button
          data-testid="publish-all"
          onClick={() => bulkPublish(true)}
          disabled={busy}
          className="btn-ghost text-xs"
          title="Publicar TODAS as categorias"
        >
          Publicar Todos
        </button>
        <button
          data-testid="hide-all"
          onClick={() => bulkPublish(false)}
          disabled={busy}
          className="btn-ghost text-xs"
          title="Ocultar TODAS as categorias"
        >
          Ocultar Todos
        </button>
      </div>

      {data && (
        <div className="card overflow-x-auto">
          <table className="table-dense">
            <thead>
              <tr>
                <th className="w-16">Pos.</th>
                <th className="w-14">Nº</th>
                <th>Atleta</th>
                <th>Equipe</th>
                {data.rounds.map((r) => (
                  <th key={r} className="tabular-nums text-center">
                    R{r}
                  </th>
                ))}
                <th className="tabular-nums text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.ranking.map((row) => (
                <tr key={row.athlete_id} data-testid={`rank-row-${row.athlete_id}`}>
                  <td>
                    {row.posicao ? (
                      row.posicao <= 3 ? (
                        <span
                          className="font-display text-2xl font-black tabular-nums"
                          style={{ color: MEDAL_COLORS[row.posicao - 1] }}
                        >
                          {row.posicao}º
                        </span>
                      ) : (
                        <span className="font-display text-xl font-bold tabular-nums">
                          {row.posicao}º
                        </span>
                      )
                    ) : row.wo ? (
                      <span data-testid={`rank-wo-${row.athlete_id}`} className="text-[10px] font-black tracking-widest px-1.5 py-0.5 bg-[#FF453A] text-white">
                        W.O.
                      </span>
                    ) : (
                      <span className="text-[var(--text-secondary)]">—</span>
                    )}
                  </td>
                  <td className="tabular-nums font-display font-bold">{row.numero}</td>
                  <td className={row.wo ? "line-through opacity-60" : ""}>{row.nome}<AthleteBadges athlete={{ ...row, id: row.athlete_id }} /></td>
                  <td className="text-xs text-[var(--text-secondary)]">{row.equipe || "—"}</td>
                  {data.rounds.map((r) => {
                    const rd = row.rounds[String(r)] || {};
                    return (
                      <td key={r} className="text-center tabular-nums text-sm">
                        {rd.rank != null ? (
                          <>
                            <span className="font-bold">{rd.rank}</span>
                            <span className="text-[var(--text-secondary)] ml-1 text-xs">
                              ({rd.total})
                            </span>
                          </>
                        ) : (
                          <span className="text-[var(--text-secondary)]">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-center tabular-nums font-display font-bold text-lg">
                    {row.score_final ?? "—"}
                  </td>
                </tr>
              ))}
              {data.ranking.length === 0 && (
                <tr>
                  <td colSpan={5 + data.rounds.length} className="text-center py-8 text-[var(--text-secondary)]">
                    Nenhum dado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
